import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { MAPS } from '@stillsun/shared';
import type { DeathPoint } from '../lib/types.js';

const CANVAS_W = 640;
const MIN_H = 260;
const MAX_H = 520;

// Sequential single-hue ramp (amber, low→high) for death density. Never a rainbow.
const RAMP: Array<{ t: number; rgba: [number, number, number, number] }> = [
  { t: 0, rgba: [120, 60, 20, 0] },
  { t: 0.18, rgba: [120, 60, 20, 70] },
  { t: 0.45, rgba: [201, 122, 51, 150] },
  { t: 0.75, rgba: [255, 160, 64, 220] },
  { t: 1, rgba: [255, 235, 200, 255] },
];

function rampColor(t: number): [number, number, number, number] {
  const clamped = Math.max(0, Math.min(1, t));
  for (let i = 1; i < RAMP.length; i++) {
    const prev = RAMP[i - 1]!;
    const cur = RAMP[i]!;
    if (clamped <= cur.t) {
      const span = cur.t - prev.t || 1;
      const f = (clamped - prev.t) / span;
      return [
        prev.rgba[0] + (cur.rgba[0] - prev.rgba[0]) * f,
        prev.rgba[1] + (cur.rgba[1] - prev.rgba[1]) * f,
        prev.rgba[2] + (cur.rgba[2] - prev.rgba[2]) * f,
        prev.rgba[3] + (cur.rgba[3] - prev.rgba[3]) * f,
      ];
    }
  }
  return RAMP[RAMP.length - 1]!.rgba;
}

type KillerFilter = 'all' | 'player' | 'hymn';

export function DeathHeatmap({ mapId, deaths }: { mapId: string; deaths: DeathPoint[] }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [filter, setFilter] = useState<KillerFilter>('all');
  const map = MAPS[mapId];

  const filtered = useMemo(
    () => (filter === 'all' ? deaths : deaths.filter((d) => d.killer === filter)),
    [deaths, filter],
  );

  const bounds = useMemo(() => {
    if (!map) return null;
    let minX = Infinity;
    let maxX = -Infinity;
    let minZ = Infinity;
    let maxZ = -Infinity;
    const extend = (x: number, z: number) => {
      minX = Math.min(minX, x);
      maxX = Math.max(maxX, x);
      minZ = Math.min(minZ, z);
      maxZ = Math.max(maxZ, z);
    };
    for (const box of map.world.boxes) {
      extend(box.min.x, box.min.z);
      extend(box.max.x, box.max.z);
    }
    for (const s of map.spawns) extend(s.x, s.z);
    for (const d of deaths) extend(d.x, d.z);
    if (!Number.isFinite(minX)) return null;
    const pad = 4;
    return { minX: minX - pad, maxX: maxX + pad, minZ: minZ - pad, maxZ: maxZ + pad };
  }, [map, deaths]);

  const canvasH = useMemo(() => {
    if (!bounds) return MIN_H;
    const w = bounds.maxX - bounds.minX || 1;
    const h = bounds.maxZ - bounds.minZ || 1;
    return Math.round(Math.min(MAX_H, Math.max(MIN_H, CANVAS_W * (h / w))));
  }, [bounds]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !map || !bounds) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = CANVAS_W;
    canvas.height = canvasH;

    const spanX = bounds.maxX - bounds.minX || 1;
    const spanZ = bounds.maxZ - bounds.minZ || 1;
    const scale = Math.min(CANVAS_W / spanX, canvasH / spanZ);
    const offX = (CANVAS_W - spanX * scale) / 2;
    const offZ = (canvasH - spanZ * scale) / 2;
    const toCanvas = (x: number, z: number): [number, number] => [
      offX + (x - bounds.minX) * scale,
      offZ + (z - bounds.minZ) * scale,
    ];

    // Background + map footprint (cover geometry, top-down).
    ctx.fillStyle =
      getComputedStyle(document.documentElement).getPropertyValue('--char') || '#1c1613';
    ctx.fillRect(0, 0, CANVAS_W, canvasH);
    ctx.fillStyle = 'rgba(156, 139, 125, 0.18)';
    ctx.strokeStyle = 'rgba(156, 139, 125, 0.4)';
    ctx.lineWidth = 1;
    for (const box of map.world.boxes) {
      const [x0, y0] = toCanvas(box.min.x, box.min.z);
      const [x1, y1] = toCanvas(box.max.x, box.max.z);
      ctx.fillRect(x0, y0, x1 - x0, y1 - y0);
      ctx.strokeRect(x0, y0, x1 - x0, y1 - y0);
    }

    if (filtered.length === 0) return;

    // Density pass: accumulate soft blobs on an offscreen canvas, additive.
    const density = document.createElement('canvas');
    density.width = CANVAS_W;
    density.height = canvasH;
    const dctx = density.getContext('2d');
    if (!dctx) return;
    dctx.globalCompositeOperation = 'lighter';
    const radius = Math.max(10, scale * 2.5);
    for (const d of filtered) {
      const [cx, cy] = toCanvas(d.x, d.z);
      const grad = dctx.createRadialGradient(cx, cy, 0, cx, cy, radius);
      grad.addColorStop(0, 'rgba(255,255,255,0.55)');
      grad.addColorStop(1, 'rgba(255,255,255,0)');
      dctx.fillStyle = grad;
      dctx.beginPath();
      dctx.arc(cx, cy, radius, 0, Math.PI * 2);
      dctx.fill();
    }

    const img = dctx.getImageData(0, 0, CANVAS_W, canvasH);
    const out = ctx.createImageData(CANVAS_W, canvasH);
    let maxA = 1;
    for (let i = 3; i < img.data.length; i += 4) {
      const a = img.data[i];
      if (a !== undefined && a > maxA) maxA = a;
    }
    for (let i = 0; i < img.data.length; i += 4) {
      const a = img.data[i + 3] ?? 0;
      if (a === 0) continue;
      const t = a / maxA;
      const [r, g, b, oa] = rampColor(t);
      out.data[i] = r;
      out.data[i + 1] = g;
      out.data[i + 2] = b;
      out.data[i + 3] = oa;
    }
    ctx.putImageData(out, 0, 0);
  }, [map, bounds, canvasH, filtered]);

  if (!map) {
    return <div class="empty">Unknown map "{mapId}"</div>;
  }

  return (
    <div>
      <div class="row" style={{ marginBottom: '8px' }}>
        {(['all', 'player', 'hymn'] as const).map((k) => (
          <button
            key={k}
            type="button"
            class={`chip chip-btn${filter === k ? ' active' : ''}`}
            onClick={() => setFilter(k)}
          >
            {k === 'all' ? 'All deaths' : `By ${k}`}
          </button>
        ))}
        <span class="hint">{filtered.length} deaths</span>
      </div>
      <canvas
        ref={canvasRef}
        style={{
          width: '100%',
          maxWidth: `${CANVAS_W}px`,
          height: 'auto',
          display: 'block',
          borderRadius: '6px',
          border: '1px solid var(--border)',
        }}
      />
      <div class="legend">
        <span>Cold</span>
        <span
          style={{
            display: 'inline-block',
            width: '120px',
            height: '8px',
            borderRadius: '4px',
            background: 'linear-gradient(90deg, rgba(120,60,20,0), #c97a33, #ffa040, #ffebc8)',
          }}
        />
        <span>Hot</span>
      </div>
    </div>
  );
}
